import React from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import Button from 'components/lib/Button';

import { useCMASummaryContext } from './CMASummaryContext';
import CMASummaryTableTitle from './CMASummaryTableTitle';
import {
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
} from './constants';
import { generateLabelDataTestId } from './testIdUtils';
import { getCMAChildrenRows, isRowExpandable } from './utils';

interface TitleProps {
  level: number;
}

const Container = styled.div<TitleProps>`
  display: flex;
  flex-flow: column;
  padding-left: ${(props) => `${(props?.level ?? 0) * 30}px`};

  &.indented {
    padding-left: 30px;
  }
`;

interface ButtonProps {
  level: 1 | 2 | 3;
}

const StyledButton = styled(Button) <ButtonProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${(props) => `${22 * props.level}px`};
  padding: 0px;
  height: 16px;
  width: 16px;
  box-shadow: none;
`;

type Props = {
  cmaSummaryRow: any;
  expandedRowKeys: string[];
  setExpandedRowKeys: (keys: string[]) => void;
};

const CMASummaryTableTitleColumn = ({ cmaSummaryRow, expandedRowKeys, setExpandedRowKeys }: Props) => {
  const { data } = useCMASummaryContext();
  const rowMetadata = cmaSummaryRow[0];
  const title = rowMetadata?.title || null;
  const titleMeta = rowMetadata?.titleMeta || null;
  const subtitle = rowMetadata?.subtitle || null;

  const toggleExpanded = (rowKey: string) => {
    const newExpandedKeys = new Set(expandedRowKeys);

    const collapseKeyAndChildren = (key: string) => {
      newExpandedKeys.delete(key);
      const childrenRows = getCMAChildrenRows(key, data);
      childrenRows.forEach((row) => {
        const childRowMetadata = row[0];
        if (newExpandedKeys.has(childRowMetadata.key)) {
          collapseKeyAndChildren(childRowMetadata.key);
        }
      });
    };

    if (newExpandedKeys.has(rowKey)) {
      collapseKeyAndChildren(rowKey);
    } else {
      newExpandedKeys.add(rowKey);
    }

    setExpandedRowKeys(Array.from(newExpandedKeys));
  };

  const cellClasses = classNames({
    indented:
      [
        CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
        CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
        CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
        CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
      ].includes(rowMetadata?.type) && !rowMetadata?.level,
  });

  return (
    <Container level={rowMetadata?.level} className={cellClasses}>
      {isRowExpandable(rowMetadata.type) && Boolean(rowMetadata?.level) && (
        <StyledButton type="text" onClick={() => toggleExpanded(rowMetadata.key)} level={rowMetadata.level}>
          <img
            src={expandedRowKeys.includes(rowMetadata.key) ? '/icons/expand-more.svg' : '/icons/chevron-right.svg'}
            style={{ width: '16px', height: '16px' }}
          />
        </StyledButton>
      )}
      <CMASummaryTableTitle
        rowType={rowMetadata?.type}
        rowKey={rowMetadata?.key}
        title={title}
        titleMeta={titleMeta}
        subtitle={subtitle}
        dataTestId={generateLabelDataTestId(rowMetadata)}
        expandedRowKeys={expandedRowKeys}
        toggleExpanded={toggleExpanded}
      />
    </Container>
  );
};

export default CMASummaryTableTitleColumn;
